import React, { useState, useEffect, useMemo } from 'react';
import { useAutoScroll } from '../hooks/useAutoScroll';
import {
  inclusiveEndForCoverPeriod,
  compareYmd,
  getTodayYmdLocal,
  getTpoExtendStartWindow,
  isTpoExtendStartAllowed
} from '../utils/calendarDates';

import {
  INSTITUTION_TYPE_SCHOOL,
  INSTITUTION_TYPE_OTHER,
  getInstitutionalPllFee,
  institutionalPllLabel
} from '../utils/institutionalPll';

// Start date: selectable range is 30 days as of today (today through today + 30 days).
// Cover period (end date) is always calculated from the chosen start date (1 month or 1 year from that date).
const getTodayISO = () => getTodayYmdLocal() || new Date().toISOString().split('T')[0];
const getMaxStartDateISO = () => {
  const today = getTodayISO();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!m) {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  }
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 30)).toISOString().slice(0, 10);
};
const isStartDateWithin30Days = (dateStr) => {
  if (!dateStr) return false;
  const today = getTodayISO();
  const max = getMaxStartDateISO();
  const chosen = String(dateStr).trim().slice(0, 10);
  return chosen >= today && chosen <= max;
};

const MOTORCYCLE_PSV_ANNUAL = 'Motorcycle PSV Annual';
const MOTORCYCLE_PSV_6_MONTHS = 'Motorcycle PSV 6 Months';

/** Second 50% on an existing portal policy (finder flow): remaining annual period on the certificate. */
const TPO_EXTEND_SECOND_HALF_PERIOD = '1 Year (50%)';
/** Start-new extendable quote: no portal policy yet — one calendar month of TPO cover. */
const TPO_EXTEND_FRESH_FIXED_PERIOD = '1 Month';

const normalizeVehicleClassForSubmit = (className) => {
  if (className === MOTORCYCLE_PSV_ANNUAL || className === MOTORCYCLE_PSV_6_MONTHS) return 'Motorcycle PSV';
  return className;
};

const forcedPeriodForClass = (className) => {
  if (className === MOTORCYCLE_PSV_ANNUAL) return '1 Year';
  if (className === MOTORCYCLE_PSV_6_MONTHS) return '6 Months';
  return null;
};

const displayClassFromInitialData = (className, period) => {
  if (className === 'Motorcycle PSV') {
    if (period === '6 Months') return MOTORCYCLE_PSV_6_MONTHS;
    return MOTORCYCLE_PSV_ANNUAL;
  }
  return className || '';
};

const CoverDetails = ({
  coverType,
  classes,
  pricing,
  initialData,
  onComplete,
  onBack,
  fixedPeriod = null,
  lockFields = false,
  continueLabel = 'Continue to Insured Details →',
  /** TPO_EXTEND: last day of the annual policy (portal: first segment start + 1 year − 1 day). */
  extensionAnnualEndDate = null,
  /** TPO_EXTEND: portal first-segment start (anchors the initial 1-month certificate end). */
  extensionFirstSegmentStartDate = null,
  /** TPO/TOR renewal: do not start before the day after the current cover ends. */
  renewalEarliestStart = null
}) => {
  const [selectedClass, setSelectedClass] = useState(displayClassFromInitialData(initialData?.selectedClass, initialData?.coverPeriod));
  const [institutionType, setInstitutionType] = useState(
    initialData?.institutionType === INSTITUTION_TYPE_OTHER ? INSTITUTION_TYPE_OTHER : (initialData?.institutionType === INSTITUTION_TYPE_SCHOOL ? INSTITUTION_TYPE_SCHOOL : '')
  );
  const [startDate, setStartDate] = useState(initialData?.startDate || '');
  // Auto-set cover period to "1 Month" for TOR
  const [coverPeriod, setCoverPeriod] = useState(initialData?.coverPeriod || (coverType === 'TOR' ? '1 Month' : fixedPeriod || ''));
  const [endDate, setEndDate] = useState(initialData?.endDate || '');
  const [startDateError, setStartDateError] = useState('');
  const [extensionRangeError, setExtensionRangeError] = useState('');
  const forcedClassPeriod = forcedPeriodForClass(selectedClass);

  const startDateInputBounds = useMemo(() => {
    if (coverType === 'TPO_EXTEND' && extensionAnnualEndDate) {
      const w = getTpoExtendStartWindow(extensionAnnualEndDate, getTodayYmdLocal(), extensionFirstSegmentStartDate);
      if (!w || w.empty) {
        return { min: extensionAnnualEndDate, max: extensionAnnualEndDate };
      }
      return { min: w.earliest, max: w.latest };
    }
    const minToday = getTodayISO();
    const earliest = renewalEarliestStart && compareYmd(renewalEarliestStart, minToday) > 0
      ? renewalEarliestStart
      : minToday;
    return { min: earliest, max: getMaxStartDateISO() };
  }, [coverType, extensionAnnualEndDate, extensionFirstSegmentStartDate, renewalEarliestStart]);

  const standardStartError = (value) => {
    if (!value) return '';
    if (!isStartDateWithin30Days(value)) return 'Start date must be within 30 days of today.';
    if (renewalEarliestStart && compareYmd(String(value).slice(0, 10), renewalEarliestStart) < 0) {
      return `Renewal cannot start before ${renewalEarliestStart}. That is the day after the current cover ends.`;
    }
    return '';
  };

  // Keep local state in sync with parent when user navigates back to this step (e.g. from "Choose my own date")
  useEffect(() => {
    if (initialData?.startDate && initialData.startDate !== startDate) {
      setStartDate(initialData.startDate);
    }
    if (coverType === 'TPO_EXTEND' && extensionAnnualEndDate) {
      if (endDate !== extensionAnnualEndDate) setEndDate(extensionAnnualEndDate);
    } else if (initialData?.endDate && initialData.endDate !== endDate) {
      setEndDate(initialData.endDate);
    }
    const initialDisplayClass = displayClassFromInitialData(initialData?.selectedClass, initialData?.coverPeriod);
    const classForPeriodLock = selectedClass || initialDisplayClass;
    const forcedPeriodForActiveClass = forcedPeriodForClass(classForPeriodLock);

    // TPO_EXTEND: period is fixed per flow (1 Month start-new vs 1 Year (50%) second half); do not let stale initialData override.
    if (coverType === 'TPO_EXTEND' && fixedPeriod) {
      if (coverPeriod !== fixedPeriod) setCoverPeriod(fixedPeriod);
    } else if (coverType === 'TOR') {
      if (coverPeriod !== '1 Month') setCoverPeriod('1 Month');
    } else if (forcedPeriodForActiveClass) {
      if (coverPeriod !== forcedPeriodForActiveClass) setCoverPeriod(forcedPeriodForActiveClass);
    } else if (
      initialData?.coverPeriod != null &&
      String(initialData.coverPeriod).trim() !== '' &&
      initialData.coverPeriod !== coverPeriod
    ) {
      // Only sync non-empty parent values — parent often keeps coverPeriod as '' until Continue, and including
      // local coverPeriod in this effect's deps would re-run after each user pick and wipe the selection.
      setCoverPeriod(initialData.coverPeriod);
    }
    if (initialDisplayClass && initialDisplayClass !== selectedClass) {
      setSelectedClass(initialDisplayClass);
    }
    if (initialData?.institutionType && initialData.institutionType !== institutionType) {
      setInstitutionType(initialData.institutionType);
    }
  }, [
    initialData?.startDate,
    initialData?.endDate,
    initialData?.coverPeriod,
    initialData?.selectedClass,
    initialData?.institutionType,
    coverType,
    extensionAnnualEndDate,
    endDate,
    fixedPeriod
  ]);

  // Auto-scroll setup - for TOR, skip coverPeriod since it's auto-filled
  const fieldOrder = (coverType === 'TOR' || fixedPeriod)
    ? ['vehicleClass', 'startDate'] 
    : ['vehicleClass', 'startDate', 'coverPeriod'];
  
  const isFieldValid = (fieldName, value) => {
    if (!value || value.toString().trim() === '') return false;

    switch (fieldName) {
      case 'vehicleClass':
        return value.trim().length > 0;
      case 'startDate':
        return value.trim().length > 0;
      case 'coverPeriod':
        return value.trim().length > 0;
      default:
        return false;
    }
  };

  const { 
    initializeFieldStates, 
    updateFieldValidationState,
    handleFocus,
    scrollToTop
  } = useAutoScroll(fieldOrder, isFieldValid);

  // Initialize field states and scroll to top on component mount
  useEffect(() => {
    initializeFieldStates(fieldOrder);
    scrollToTop();
    if (coverType === 'TOR' && !coverPeriod) setCoverPeriod('1 Month');
    if (fixedPeriod && coverPeriod !== fixedPeriod) setCoverPeriod(fixedPeriod);
    // Do not list local coverPeriod as a dependency — it would re-run scrollToTop and field init on every period change and fight the dropdown.
  }, [initializeFieldStates, scrollToTop, coverType, fixedPeriod]);

  // Validate start date: TPO_EXTEND uses local calendar + backdate window; others use 30 days from Nairobi today.
  useEffect(() => {
    if (coverType === 'TPO_EXTEND' && extensionAnnualEndDate) {
      setStartDateError('');
      return;
    }
    if (startDate) {
      setStartDateError(standardStartError(startDate));
    }
  }, [coverType, extensionAnnualEndDate, startDate, renewalEarliestStart]);

  // Update select elements to have has-value class when they have a value
  useEffect(() => {
    const vehicleClassSelect = document.getElementById('vehicleClass');
    const coverPeriodSelect = document.getElementById('coverPeriod');
    
    if (vehicleClassSelect) {
      if (selectedClass) {
        vehicleClassSelect.classList.add('has-value');
      } else {
        vehicleClassSelect.classList.remove('has-value');
      }
    }
    
    // For TOR, coverPeriod is always set, so always mark as has-value
    if (coverPeriodSelect) {
      if (coverPeriod || coverType === 'TOR' || fixedPeriod) {
        coverPeriodSelect.classList.add('has-value');
      } else {
        coverPeriodSelect.classList.remove('has-value');
      }
    }
  }, [selectedClass, coverPeriod, coverType, fixedPeriod]);

  // Auto-set cover period to "1 Month" for TOR when start date is entered
  useEffect(() => {
    if (coverType === 'TOR' && startDate && !coverPeriod) {
      setCoverPeriod('1 Month');
    }
    if (forcedClassPeriod && coverPeriod !== forcedClassPeriod) {
      setCoverPeriod(forcedClassPeriod);
    }
    if (fixedPeriod && coverPeriod !== fixedPeriod) setCoverPeriod(fixedPeriod);
  }, [coverType, startDate, coverPeriod, fixedPeriod, forcedClassPeriod]);

  // End date = 1 month or 1 year from the chosen start date (UTC calendar math — not local Date parsing).
  // TPO_EXTEND: displayed end is the annual policy last day from portal first segment + 1 year (not "extension start + 1 month").
  useEffect(() => {
    if (coverType === 'TPO_EXTEND' && extensionAnnualEndDate) {
      setEndDate(extensionAnnualEndDate);
      return;
    }
    const periodToUse = coverType === 'TOR' ? '1 Month' : (fixedPeriod || coverPeriod);
    if (startDate && periodToUse) {
      const calculatedEndDate = inclusiveEndForCoverPeriod(startDate, periodToUse);
      setEndDate(calculatedEndDate);
    }
  }, [startDate, coverPeriod, coverType, fixedPeriod, extensionAnnualEndDate]);

  useEffect(() => {
    if (coverType !== 'TPO_EXTEND' || !extensionAnnualEndDate || !startDate) {
      setExtensionRangeError('');
      return;
    }
    const todayLoc = getTodayYmdLocal();
    const w = getTpoExtendStartWindow(extensionAnnualEndDate, todayLoc, extensionFirstSegmentStartDate);
    if (!w) {
      setExtensionRangeError('');
      return;
    }
    if (w.empty) {
      setExtensionRangeError(
        `Annual end ${extensionAnnualEndDate} is before the earliest allowed start (${w.earliest}). Underwriting must fix dates.`
      );
      return;
    }
    if (compareYmd(startDate, extensionAnnualEndDate) > 0) {
      setExtensionRangeError(`Start must be on or before annual end ${extensionAnnualEndDate}.`);
      return;
    }
    if (compareYmd(startDate, w.earliest) < 0 || compareYmd(startDate, w.latest) > 0) {
      setExtensionRangeError(`Start must be between ${w.earliest} and ${w.latest}.`);
      return;
    }
    setExtensionRangeError('');
  }, [coverType, extensionAnnualEndDate, extensionFirstSegmentStartDate, startDate]);

  const handleContinue = () => {
    if (startDateError) return;
    if (coverType === 'TPO_EXTEND' && extensionRangeError) return;
    const requiredPeriod = coverType === 'TOR' ? '1 Month' : (fixedPeriod || forcedClassPeriod);
    const periodValid = requiredPeriod ? coverPeriod === requiredPeriod : !!coverPeriod;
    const startOk =
      coverType === 'TPO_EXTEND' && extensionAnnualEndDate
        ? isTpoExtendStartAllowed(startDate, extensionAnnualEndDate, getTodayYmdLocal(), extensionFirstSegmentStartDate)
        : !!startDate && standardStartError(startDate) === '';
    if (selectedClass && startDate && periodValid && startOk) {
      const isInstitutional = normalizeVehicleClassForSubmit(selectedClass) === 'Motor Commercial Institutional';
      if (isInstitutional && !institutionType) return;
      onComplete(
        normalizeVehicleClassForSubmit(selectedClass),
        startDate,
        coverPeriod,
        isInstitutional ? institutionType : null
      );
    }
  };

  const isFormComplete = () => {
    const requiredPeriod = coverType === 'TOR' ? '1 Month' : (fixedPeriod || forcedClassPeriod);
    const periodValid = requiredPeriod ? coverPeriod === requiredPeriod : !!coverPeriod;
    const hasRequired = selectedClass && startDate && periodValid;
    const institutionalOk =
      normalizeVehicleClassForSubmit(selectedClass) !== 'Motor Commercial Institutional' || !!institutionType;
    const extendOk = coverType !== 'TPO_EXTEND' || !extensionRangeError;
    const startOk =
      coverType === 'TPO_EXTEND' && extensionAnnualEndDate
        ? isTpoExtendStartAllowed(startDate, extensionAnnualEndDate, getTodayYmdLocal(), extensionFirstSegmentStartDate)
        : !!startDate && standardStartError(startDate) === '';
    return hasRequired && institutionalOk && !startDateError && startOk && extendOk;
  };

  const getClassDescription = (className) => {
    const descriptions = {
      'Motor Private': 'Personal vehicle for private use - lowest risk category',
      'Motor Commercial Institutional': 'Company vehicles for institutional purposes',
      'Motor Commercial General Cartage': 'General cargo transport vehicles',
      'Motor Commercial Own Goods': 'Vehicles transporting your own goods',
      'Motorcycle Private': 'Personal motorcycles for private use',
      'Motorcycle PSV': 'Motorcycles used for public service (taxis, deliveries)',
      [MOTORCYCLE_PSV_ANNUAL]: 'Motorcycle PSV annual product',
      [MOTORCYCLE_PSV_6_MONTHS]: 'Motorcycle PSV six-month product',
      'PSV Chauffer Driven': 'Public service vehicles with professional drivers',
      'Motor Commercial': 'General commercial vehicle coverage',
      'Tractor TPO': 'Agricultural / commercial tractors — third party, annual',
      'Tuk Tuk Commercial': 'Three-wheeler used for own goods or commercial (non-PSV)',
      'Tuk Tuk PSV': 'Three-wheeler licensed for passenger service (PSV)'
    };
    
    return descriptions[className] || 'Comprehensive vehicle insurance coverage';
  };

  const getAvailablePeriods = (className) => {
    if (coverType === 'TOR' || coverType === 'COMP') {
      return ['1 Month']; // Strictly 1 month only
    }
    if (fixedPeriod) return [fixedPeriod];
    
    // TPO only offers 1 Year - strictly enforced
    if (coverType === 'TPO') {
      const forced = forcedPeriodForClass(className);
      if (forced) return [forced];
      if (className === 'Motorcycle PSV') return ['1 Year', '6 Months'];
      return ['1 Year'];
    }
    
    // Ensure className is a string before calling includes
    if (!className || typeof className !== 'string') {
      return ['1 Year'];
    }
    
    return ['1 Year'];
  };

  const getPricingInfo = (className, period) => {
    if (!className || !period || typeof className !== 'string') return null;
    
    const normalizedClass = normalizeVehicleClassForSubmit(className);
    const matchingPrice = pricing.find(price => 
      price.label === normalizedClass && price.period === period
    );
    
    return matchingPrice;
  };

  const pricingInfo = getPricingInfo(selectedClass, coverPeriod);

  const isTpoExtendSecondHalfFlow =
    coverType === 'TPO_EXTEND' && !!extensionAnnualEndDate && fixedPeriod === TPO_EXTEND_SECOND_HALF_PERIOD;
  const isTpoExtendFreshOneMonthFlow =
    coverType === 'TPO_EXTEND' && !extensionAnnualEndDate && fixedPeriod === TPO_EXTEND_FRESH_FIXED_PERIOD;

  const coverPeriodReadonlyDisplay =
    coverType === 'TOR' || coverType === 'COMP'
      ? '1 Month'
      : isTpoExtendSecondHalfFlow
        ? 'Remaining period (50%)'
        : fixedPeriod || forcedClassPeriod || coverPeriod;

  return (
    <div className="cover-details">
      <h1 className="main-title">Cover Details</h1>
      <p className="subtitle">
        {isTpoExtendSecondHalfFlow
          ? 'Complete your TPO extension: you pay the second 50% and the certificate covers the rest of the annual period, through the end date below.'
          : isTpoExtendFreshOneMonthFlow
            ? 'New extendable TPO quote: one month of cover to start; you can use Buy/Extend later for the second 50% when you have a Monarch annual policy on file.'
            : `Configure your ${coverType === 'TPO_EXTEND' ? 'TPO' : coverType} insurance cover details`}
      </p>

      <div className="cover-form">
        {/* Class Selection */}
        <div className="form-section">
          <h3>🚗 Vehicle Class Selection</h3>
          <div className="form-group">
            <label htmlFor="vehicleClass">Select Vehicle Class *</label>
            <select
              id="vehicleClass"
              value={selectedClass}
              onChange={(e) => {
                const value = e.target.value;

                setSelectedClass(value);
                if (normalizeVehicleClassForSubmit(value) !== 'Motor Commercial Institutional') {
                  setInstitutionType('');
                }
                const forced = forcedPeriodForClass(value);
                // Reset period when class changes, but keep forced period products and TOR defaults
                if (coverType === 'TOR') {
                  setCoverPeriod('1 Month');
                } else if (coverType === 'TPO_EXTEND' && fixedPeriod) {
                  setCoverPeriod(fixedPeriod);
                } else if (forced) {
                  setCoverPeriod(forced);
                } else {
                  setCoverPeriod('');
                }
                // Trigger auto-scroll validation using the hook
                updateFieldValidationState('vehicleClass', value);
              }}
              onFocus={() => handleFocus('vehicleClass')}
              className={selectedClass ? 'has-value' : ''}
              required
              disabled={lockFields}
            >
              <option value="">Choose your vehicle class</option>
              {classes.map((className, index) => (
                <option key={index} value={className}>
                  {className}
                </option>
              ))}
            </select>
            {selectedClass && (
              <div className="class-description-box">
                <p>{getClassDescription(selectedClass)}</p>
              </div>
            )}
          </div>
          {normalizeVehicleClassForSubmit(selectedClass) === 'Motor Commercial Institutional' && (
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label htmlFor="institutionType">Institution type *</label>
              <select
                id="institutionType"
                value={institutionType}
                onChange={(e) => setInstitutionType(e.target.value)}
                className={institutionType ? 'has-value' : ''}
                required
                disabled={lockFields && !!institutionType}
              >
                <option value="">Choose institution type</option>
                <option value={INSTITUTION_TYPE_SCHOOL}>
                  Schools (PLL KES {getInstitutionalPllFee(INSTITUTION_TYPE_SCHOOL)} / passenger)
                </option>
                <option value={INSTITUTION_TYPE_OTHER}>
                  Other institutions (PLL KES {getInstitutionalPllFee(INSTITUTION_TYPE_OTHER)} / passenger)
                </option>
              </select>
              {!institutionType ? (
                <small>
                  Choose institution type to continue. This sets the passenger liability rate for the second 50%.
                </small>
              ) : lockFields ? (
                <small>Taken from the first cover on this policy.</small>
              ) : null}
              {institutionType && (
                <div className="class-description-box">
                  <p>
                    {institutionalPllLabel(institutionType)}: passenger liability KES{' '}
                    {getInstitutionalPllFee(institutionType)} per passenger. Product remains Institutional (0813).
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Date Selection */}
        <div className="form-section">
          <h3>
            {isTpoExtendSecondHalfFlow
              ? '📅 Extension period & dates'
              : isTpoExtendFreshOneMonthFlow
                ? '📅 Cover period & dates'
                : '📅 Cover Period & Dates'}
          </h3>
          
          <div className="date-selection-grid">
            <div className="form-group">
              <label htmlFor="startDate">
                {coverType === 'TPO_EXTEND' && extensionAnnualEndDate ? 'Extension start *' : 'Start Date *'}
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                min={startDateInputBounds.min}
                max={startDateInputBounds.max}
                onChange={(e) => {
                  const value = e.target.value;
                  setStartDate(value);
                  if (value) {
                    if (coverType === 'TPO_EXTEND' && extensionAnnualEndDate) {
                      setStartDateError('');
                    } else {
                      setStartDateError(standardStartError(value));
                    }
                  } else {
                    setStartDateError('');
                  }
                  if (coverType === 'TOR' && value && !coverPeriod) {
                    setCoverPeriod('1 Month');
                  }
                  updateFieldValidationState('startDate', value);
                }}
                onBlur={() => {
                  if (coverType === 'TPO_EXTEND' && extensionAnnualEndDate) {
                    if (
                      startDate &&
                      !isTpoExtendStartAllowed(
                        startDate,
                        extensionAnnualEndDate,
                        getTodayYmdLocal(),
                        extensionFirstSegmentStartDate
                      )
                    ) {
                      setStartDateError('');
                    }
                  } else if (startDate) {
                    setStartDateError(standardStartError(startDate));
                  }
                }}
                onFocus={() => handleFocus('startDate')}
                required
                disabled={lockFields && coverType !== 'TPO_EXTEND'}
                aria-invalid={!!startDateError}
                aria-describedby={startDateError ? 'startDate-error' : undefined}
              />
              {startDateError && (
                <p id="startDate-error" className="form-error" role="alert" style={{ marginTop: '6px', color: '#dc3545', fontSize: '13px' }}>
                  {startDateError}
                </p>
              )}
              <small>
                {renewalEarliestStart
                  ? `Renewal start is from ${renewalEarliestStart} and must stay within 30 days of today, so it does not overlap the current cover.`
                  : coverType === 'TOR'
                  ? 'Within 30 days of today; cover period is 1 month.'
                  : coverType === 'TPO_EXTEND'
                    ? isTpoExtendSecondHalfFlow
                      ? 'Cover runs from this date through the annual policy end below (the rest of the period you are paying for). Earliest: today if the initial certificate has ended; otherwise the day after it ends (or tomorrow). Latest: the earlier of 30 days ahead or the annual policy end below.'
                      : isTpoExtendFreshOneMonthFlow
                        ? 'Start within 30 days of today. End date is one calendar month from your start date.'
                        : 'Earliest: today if the initial certificate has ended; otherwise the day after it ends (or tomorrow). Latest: the earlier of 30 days ahead or the annual end below.'
                    : 'Within 30 days of today.'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="coverPeriod">
                {isTpoExtendSecondHalfFlow ? 'Extension term *' : 'Cover Period *'}
              </label>
              {(coverType === 'TOR' || fixedPeriod || forcedClassPeriod) ? (
                // For TOR, show as read-only since it's always 1 Month
                <input
                  type="text"
                  id="coverPeriod"
                  value={coverPeriodReadonlyDisplay}
                  readOnly
                  className="readonly-input"
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed'
                  }}
                />
              ) : (
                <select
                  id="coverPeriod"
                  value={coverPeriod}
                  onChange={(e) => {
                    const value = e.target.value;

                    setCoverPeriod(value);
                    // Trigger auto-scroll validation using the hook
                    updateFieldValidationState('coverPeriod', value);
                  }}
                  onFocus={() => handleFocus('coverPeriod')}
                  className={coverPeriod ? 'has-value' : ''}
                  required
                  disabled={!selectedClass}
                >
                  <option value="">Select cover period</option>
                  {selectedClass && getAvailablePeriods(selectedClass).map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              )}
              <small>
                {coverType === 'TOR'
                  ? 'TOR covers are automatically set to 1 month'
                  : isTpoExtendSecondHalfFlow
                    ? 'This step completes your annual TPO (second 50% on Monarch). The certificate runs from your extension start through the annual end date below — the rest of the period, not a second 1-month certificate.'
                    : isTpoExtendFreshOneMonthFlow
                      ? 'One month of TPO cover (prorated from the annual rate). After Monarch holds your annual policy, you may use Buy/Extend to pay the second 50% before renewal when applicable.'
                      : coverType === 'TPO_EXTEND'
                      ? 'Second 50% of annual TPO; extension end is your policy’s annual end (not start + 1 month).'
                      : fixedPeriod
                        ? 'Cover period is fixed for this product'
                        : forcedClassPeriod
                          ? 'Cover period is fixed for this vehicle class'
                        : 'Choose available cover period for the selected product'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="endDate">
                {isTpoExtendSecondHalfFlow
                  ? 'Extension end *'
                  : isTpoExtendFreshOneMonthFlow
                    ? 'End date *'
                    : coverType === 'TPO_EXTEND'
                      ? 'Extension end *'
                      : 'End Date'}
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                readOnly
                className="readonly-input"
              />
              {isTpoExtendSecondHalfFlow ? (
                <small>
                  Last day of your annual TPO on Monarch (first segment + 1 year). This is the certificate end date — remaining cover, not a 1-month window.
                </small>
              ) : isTpoExtendFreshOneMonthFlow ? (
                <small>Automatically calculated: last day of your one-month window from the start date above.</small>
              ) : coverType !== 'TPO_EXTEND' ? (
                <small>Automatically calculated from start date and period.</small>
              ) : null}
            </div>
          </div>
        </div>

        {/* Pricing Information */}
        {pricingInfo && (
          <div className="form-section">
            <h3>💰 Premium Information</h3>
            <div className="pricing-summary">
              <div className="pricing-item">
                <span className="pricing-label">Cover Type:</span>
                <span className="pricing-value">{coverType === 'TPO_EXTEND' ? 'TPO' : coverType}</span>
              </div>
              <div className="pricing-item">
                <span className="pricing-label">Vehicle Class:</span>
                <span className="pricing-value">{selectedClass}</span>
              </div>
              <div className="pricing-item">
                    <span className="pricing-label">{isTpoExtendSecondHalfFlow ? 'Extension term:' : 'Cover Period:'}</span>
                    <span className="pricing-value">
                      {isTpoExtendSecondHalfFlow ? 'Remaining period (50%)' : coverPeriod}
                    </span>
              </div>
              {isTpoExtendSecondHalfFlow && (
                <div className="pricing-item">
                  <span className="pricing-label">Premium instalment:</span>
                  <span className="pricing-value">Second 50% of annual TPO</span>
                </div>
              )}
              {selectedClass && selectedClass.includes('Motor Commercial') && (
                <div className="pricing-note">
                  <span className="note-text">💡 Premium amount will be calculated based on vehicle tonnage in the next step</span>
                </div>
              )}
              {selectedClass && (selectedClass === 'Tractor TPO' || selectedClass === 'Tuk Tuk Commercial' || selectedClass === 'Tuk Tuk PSV') && (
                <div className="pricing-note">
                  <span className="note-text">💡 Tonnage and vehicle details in the next step are for DMVIC; premium for this product is fixed.</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {extensionRangeError && (
        <p
          className="form-error"
          role="alert"
          style={{
            margin: '0 0 12px',
            padding: '10px 12px',
            borderRadius: 6,
            background: '#fde8e8',
            border: '1px solid #f5c2c7',
            color: '#842029',
            fontSize: 14
          }}
        >
          {extensionRangeError}
        </p>
      )}

      <div className="navigation-buttons">
        <button className="btn-secondary" onClick={onBack}>
          ← Back to Cover Selection
        </button>
        <button
          id="continueButton"
          className="btn-primary"
          onClick={handleContinue}
          disabled={!isFormComplete()}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  );
};

export default CoverDetails;
